import React, {Component} from 'react';
import ReactDOM from 'react-dom';
import {API_URL} from "../../settings/local_settings";
import {APIClient} from "../../utils/APIClient";
import {Spinner} from "../Spinner";
import {FaIcon} from "../FaIcon";
import {LocalStorage} from "../../utils/LocalStorage";
import {withTranslation} from 'react-i18next';

class SuperAdminSwitcher extends Component {
    constructor(props) {
        super(props);
        this.state = {
            isOpen: false,
            // Agencies
            agencies: [],
            loadingAgencies: false,
            searchQuery: '',
            selectedAgencyId: null,
            currentAgencyId: null,
            // Clients
            clients: [],
            loadingClients: false,
            savedClientIds: [],
            selectedClientIds: [],
            // Switch
            switching: false,
            error: null
        };

        this.handleToggle = this.handleToggle.bind(this);
        this.handleSearchChange = this.handleSearchChange.bind(this);
        this.handleAgencySelect = this.handleAgencySelect.bind(this);
        this.handleClientToggle = this.handleClientToggle.bind(this);
        this.handleSelectAllClients = this.handleSelectAllClients.bind(this);
        this.handleDeselectAllClients = this.handleDeselectAllClients.bind(this);
        this.handleSwitch = this.handleSwitch.bind(this);
        this.handleOverlayClick = this.handleOverlayClick.bind(this);

        this._searchTimeout = null;
    }

    handleToggle() {
        const willOpen = !this.state.isOpen;
        this.setState({isOpen: willOpen, error: null});
        if (willOpen) {
            this.fetchAgencies('');
        }
    }

    handleOverlayClick(e) {
        if (e.target.classList.contains('super-admin-overlay')) {
            this.setState({isOpen: false});
        }
    }

    handleSearchChange(e) {
        const query = e.target.value;
        this.setState({searchQuery: query});

        if (this._searchTimeout) clearTimeout(this._searchTimeout);
        this._searchTimeout = setTimeout(() => {
            this.fetchAgencies(query);
        }, 300);
    }

    fetchAgencies(search) {
        this.setState({loadingAgencies: true});
        const url = API_URL + 'super-admin/agencies/' + (search ? '?search=' + encodeURIComponent(search) : '');
        new APIClient(url).call('GET').then((data) => {
            if (data && data.status === 'OK') {
                this.setState({
                    agencies: data.agencies,
                    currentAgencyId: data.current_agency_id,
                    loadingAgencies: false
                });
            } else {
                this.setState({loadingAgencies: false, error: data ? data.error_message : 'Error'});
            }
        });
    }

    handleAgencySelect(agencyId) {
        if (agencyId === this.state.selectedAgencyId) {
            this.setState({selectedAgencyId: null, clients: [], selectedClientIds: []});
            return;
        }
        this.setState({selectedAgencyId: agencyId, loadingClients: true, clients: [], selectedClientIds: []});
        const url = API_URL + 'super-admin/agency/' + agencyId + '/clients/';
        new APIClient(url).call('GET').then((data) => {
            if (data && data.status === 'OK') {
                const savedIds = data.saved_client_ids || [];
                this.setState({
                    clients: data.clients,
                    savedClientIds: savedIds,
                    selectedClientIds: savedIds.length > 0 ? savedIds : data.clients.map(c => c.id),
                    loadingClients: false
                });
            } else {
                this.setState({loadingClients: false, error: data ? data.error_message : 'Error'});
            }
        });
    }

    handleClientToggle(clientId) {
        this.setState(prev => {
            const ids = prev.selectedClientIds.includes(clientId)
                ? prev.selectedClientIds.filter(id => id !== clientId)
                : [...prev.selectedClientIds, clientId];
            return {selectedClientIds: ids};
        });
    }

    handleSelectAllClients() {
        this.setState({selectedClientIds: this.state.clients.map(c => c.id)});
    }

    handleDeselectAllClients() {
        this.setState({selectedClientIds: []});
    }

    handleSwitch() {
        const {selectedAgencyId, selectedClientIds} = this.state;
        if (!selectedAgencyId) return;

        this.setState({switching: true, error: null});

        const url = API_URL + 'super-admin/switch-agency/';
        new APIClient(url).call('POST', {
            agency_id: selectedAgencyId,
            client_ids: selectedClientIds
        }).then((data) => {
            if (data && data.status === 'OK') {
                LocalStorage.updateAgencyStatus(data.agency_status);
                window.location.href = '/';
            } else {
                this.setState({
                    switching: false,
                    error: data ? data.error_message : 'Error al cambiar de agencia'
                });
            }
        });
    }

    renderCurrentAgencyBadge() {
        const {t} = this.props;
        const {agencies, currentAgencyId} = this.state;
        const current = agencies.find(a => a.id === currentAgencyId);
        if (!current) return null;

        return (
            <div className="sa-current-agency">
                <FaIcon iconName="building" className="sa-current-icon"/>
                <div className="sa-current-info">
                    <span className="sa-current-label">{t('super_admin.current_agency')}</span>
                    <span className="sa-current-name">{current.name}</span>
                </div>
            </div>
        );
    }

    renderAgencyList() {
        const {t} = this.props;
        const {agencies, loadingAgencies, selectedAgencyId, currentAgencyId} = this.state;

        if (loadingAgencies) {
            return <div className="sa-loading"><Spinner color="positive small"/></div>;
        }

        if (agencies.length === 0) {
            return <div className="sa-empty">{t('super_admin.no_agencies_found')}</div>;
        }

        return (
            <div className="sa-agency-list">
                {agencies.map(agency => (
                    <div
                        key={agency.id}
                        className={
                            'sa-agency-item'
                            + (selectedAgencyId === agency.id ? ' -selected' : '')
                            + (currentAgencyId === agency.id ? ' -current' : '')
                        }
                        onClick={() => this.handleAgencySelect(agency.id)}
                    >
                        <span className="sa-agency-name">{agency.name}</span>
                        {currentAgencyId === agency.id &&
                            <span className="sa-badge-current">{t('super_admin.current')}</span>
                        }
                        {selectedAgencyId === agency.id &&
                            <FaIcon iconName="check" className="sa-check-icon"/>
                        }
                    </div>
                ))}
            </div>
        );
    }

    renderClientSelector() {
        const {t} = this.props;
        const {selectedAgencyId, clients, loadingClients, selectedClientIds, currentAgencyId} = this.state;

        if (!selectedAgencyId || selectedAgencyId === currentAgencyId) return null;

        return (
            <div className="sa-clients-section">
                <div className="sa-clients-header">
                    <span className="sa-clients-title">{t('super_admin.select_clients')}</span>
                </div>
                {loadingClients ? (
                    <div className="sa-loading"><Spinner color="positive small"/></div>
                ) : clients.length === 0 ? (
                    <div className="sa-empty">{t('super_admin.no_clients')}</div>
                ) : (
                    <>
                        <div className="sa-client-list">
                            {clients.map(client => {
                                const isSelected = selectedClientIds.includes(client.id);
                                return (
                                    <div
                                        key={client.id}
                                        className={'sa-client-item' + (isSelected ? ' -selected' : '')}
                                        onClick={() => this.handleClientToggle(client.id)}
                                    >
                                        {isSelected && <FaIcon iconName="check" className="sa-client-check-icon"/>}
                                        <span className="sa-client-name">{client.name}</span>
                                    </div>
                                );
                            })}
                        </div>
                        <div className="sa-clients-actions">
                            <button className="sa-link-btn" onClick={this.handleSelectAllClients}>
                                {t('super_admin.select_all')}
                            </button>
                            <span className="sa-separator">|</span>
                            <button className="sa-link-btn" onClick={this.handleDeselectAllClients}>
                                {t('super_admin.deselect_all')}
                            </button>
                        </div>
                    </>
                )}
            </div>
        );
    }

    renderSwitchButton() {
        const {t} = this.props;
        const {selectedAgencyId, currentAgencyId, switching} = this.state;

        if (!selectedAgencyId || selectedAgencyId === currentAgencyId) return null;

        if (switching) {
            return (
                <div className="sa-switch-row">
                    <Spinner color="positive small"/>
                </div>
            );
        }

        return (
            <div className="sa-switch-row">
                <button className="btn -alt sa-switch-btn" onClick={this.handleSwitch}>
                    <FaIcon iconName="exchange-alt" className="sa-switch-icon"/>
                    {t('super_admin.switch_agency')}
                </button>
            </div>
        );
    }

    render() {
        const {t} = this.props;
        const {isOpen, error} = this.state;

        return (
            <>
                <button className="sidebar-main-link sa-trigger" onClick={this.handleToggle}>
                    <FaIcon iconName="user-shield"/>
                    <span className="tooltip-text">{t('super_admin.title')}</span>
                </button>

                {isOpen && ReactDOM.createPortal(
                    <div className="super-admin-overlay" onClick={this.handleOverlayClick}>
                        <div className="super-admin-panel">
                            <div className="sa-panel-header">
                                <h3 className="sa-panel-title">
                                    <FaIcon iconName="user-shield" className="sa-title-icon"/>
                                    {t('super_admin.title')}
                                </h3>
                                <button className="sa-close-btn" onClick={this.handleToggle}>
                                    <FaIcon iconName="times"/>
                                </button>
                            </div>

                            <div className="sa-panel-body">
                                {this.renderCurrentAgencyBadge()}

                                {error && (
                                    <div className="sa-error">{error}</div>
                                )}

                                <div className="sa-search-box">
                                    <FaIcon iconName="search" className="sa-search-icon"/>
                                    <input
                                        type="text"
                                        className="sa-search-input"
                                        placeholder={t('super_admin.search_agency')}
                                        value={this.state.searchQuery}
                                        onChange={this.handleSearchChange}
                                        autoFocus
                                    />
                                </div>

                                {this.renderAgencyList()}
                                {this.renderClientSelector()}
                                {this.renderSwitchButton()}
                            </div>
                        </div>
                    </div>,
                    document.body
                )}
            </>
        );
    }
}

export default withTranslation()(SuperAdminSwitcher);
